import React, { useEffect, useState, useCallback } from 'react';
import { RequestQuote } from '@mui/icons-material';
import { useBudget } from '@hooks/api/useBudget';
import {
  BudgetRequest,
  BudgetRequestStatus,
  BudgetRequestRecipientStatus,
  BudgetRequestRecipient,
} from '@models/budget.model';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';

// Import components
import { BudgetSubTabNavigation } from './components/BudgetSubTabNavigation';
import { BudgetTabNavigation } from './components/BudgetTabNavigation';
import { BudgetHeader } from './components/BudgetHeader';
import { TaskProjectInfo } from './components/TaskProjectInfo';
import { DelegationInfo } from './components/DelegationInfo';
import { CancelledBudgets } from './components/CancelledBudgets';
import { BudgetRequestCard } from './components/BudgetRequestCard';
import { BudgetRequestDetails } from './components/BudgetRequestDetails';
import { EmptyBudgetState } from './components/EmptyBudgetState';
import { Button } from '@components/atoms/Button';
import { useTranslation } from '@hooks/useTranslation';

// Pure helper functions moved outside component to avoid dependency issues
const isTrulyCancelledBudget = (budgetRequest: BudgetRequest): boolean => {
  return (
    !!budgetRequest.delegationCancelledAt &&
    budgetRequest.status === BudgetRequestStatus.CANCELLED
  );
};

const isCompletedCancelledBudget = (budgetRequest: BudgetRequest): boolean => {
  return (
    !!budgetRequest.delegationCancelledAt &&
    budgetRequest.status === BudgetRequestStatus.COMPLETED
  );
};

const categorizeBudgetRequests = (
  budgetRequests: BudgetRequest[],
  orgId: string,
  isSubDelegationContext = false,
) => {
  const activeBudgets: BudgetRequest[] = [];
  const pastBudgets: BudgetRequest[] = [];
  const trulyCancelledBudgets: BudgetRequest[] = [];

  for (const request of budgetRequests || []) {
    const isOriginator = request.originatorOrganizationId === orgId;
    const isRecipientOrg = request.recipients?.some(
      (recipient: BudgetRequestRecipient) =>
        recipient.recipientOrganizationId === orgId,
    );
    const hasAcceptedRecipient = request.recipients?.some(
      (recipient: BudgetRequestRecipient) =>
        recipient.status === BudgetRequestRecipientStatus.ACCEPTED,
    );
    const isCompleted = request.status === BudgetRequestStatus.COMPLETED;
    const isActive = request.status === BudgetRequestStatus.ACTIVE;
    const isTrulyCancelled = isTrulyCancelledBudget(request);

    // Handle cancelled budgets (hide them completely)
    const isCancelled = request.status === BudgetRequestStatus.CANCELLED;
    if (isTrulyCancelled || isCancelled) {
      trulyCancelledBudgets.push(request);
      continue; // Skip cancelled budgets - don't show them at all
    }

    // Check if this budget request is relevant for our organization
    const isRelevantBudget =
      (isSubDelegationContext && isOriginator) ||
      (!isSubDelegationContext &&
        isOriginator &&
        (isActive || (isCompleted && hasAcceptedRecipient))) ||
      (isRecipientOrg && (isCompleted || isActive));

    if (isRelevantBudget) {
      // Categorize into active vs past budgets
      if (isCompleted && request.delegationCancelledAt) {
        pastBudgets.push(request);
      } else if (isActive || (isCompleted && !request.delegationCancelledAt)) {
        activeBudgets.push(request);
      }
    }
  }

  return {
    activeBudgets,
    pastBudgets,
    trulyCancelledBudgets,
  };
};

interface BudgetTabProps {
  taskId?: string;
  task?: any;
}

export const BudgetTab: React.FC<BudgetTabProps> = ({ taskId, task }) => {
  const tTaskDetail = useTranslation('taskDetail');
  const [receivedBudgetRequest, setReceivedBudgetRequest] =
    useState<BudgetRequest | null>(null);
  const [delegatedBudgetRequest, setDelegatedBudgetRequest] =
    useState<BudgetRequest | null>(null);
  const [cancelledBudgetRequests] = useState<BudgetRequest[]>([]);

  // New state for active vs past budgets
  const [activeBudgets, setActiveBudgets] = useState<{
    received: BudgetRequest | null;
    delegated: BudgetRequest | null;
  }>({ received: null, delegated: null });
  const [pastBudgets, setPastBudgets] = useState<{
    received: BudgetRequest[];
    delegated: BudgetRequest[];
  }>({ received: [], delegated: [] });
  const [loading, setLoading] = useState(false);
  const [isRecipient, setIsRecipient] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState<'received' | 'delegated'>(
    'received',
  );
  const [budgetTabType, setBudgetTabType] = useState<'active' | 'past'>(
    'active',
  );

  const { organizationId } = useAppSelector(
    (state: RootState) => state.organization,
  );
  const { fetchBudgetRequestsByTaskId } = useBudget();

  const loadBudgetData = useCallback(async () => {
    if (!taskId || !organizationId) return;

    setLoading(true);
    try {
      // Load received budget request (from originalTaskId for delegated tasks)
      if (task?.originalTaskId) {
        const originalBudgetRequests = await fetchBudgetRequestsByTaskId(
          task.originalTaskId,
        );
        const receivedCategorized = categorizeBudgetRequests(
          originalBudgetRequests,
          organizationId,
        );

        // Set active and past budgets for received
        setActiveBudgets((prev) => ({
          ...prev,
          received: receivedCategorized.activeBudgets[0] || null,
        }));
        setPastBudgets((prev) => ({
          ...prev,
          received: receivedCategorized.pastBudgets,
        }));

        // For backward compatibility
        setReceivedBudgetRequest(
          receivedCategorized.activeBudgets[0] ||
            receivedCategorized.pastBudgets[0] ||
            null,
        );
        setIsRecipient(true);
      } else {
        // For non-delegated tasks, load budget as before
        const budgetRequests = await fetchBudgetRequestsByTaskId(taskId);
        const nonDelegatedCategorized = categorizeBudgetRequests(
          budgetRequests,
          organizationId,
        );

        // Set active and past budgets for non-delegated
        setActiveBudgets((prev) => ({
          ...prev,
          received: nonDelegatedCategorized.activeBudgets[0] || null,
        }));
        setPastBudgets((prev) => ({
          ...prev,
          received: nonDelegatedCategorized.pastBudgets,
        }));

        // For backward compatibility
        setReceivedBudgetRequest(
          nonDelegatedCategorized.activeBudgets[0] ||
            nonDelegatedCategorized.pastBudgets[0] ||
            null,
        );
        setIsRecipient(false);
      }

      // Load delegated budget request if this task has been sub-delegated
      if (task?.delegateTaskId) {
        const delegatedBudgetRequests =
          await fetchBudgetRequestsByTaskId(taskId);
        const delegatedCategorized = categorizeBudgetRequests(
          delegatedBudgetRequests,
          organizationId,
          true, // Use sub-delegation context for delegated budgets
        );

        // Set active and past budgets for delegated
        setActiveBudgets((prev) => ({
          ...prev,
          delegated: delegatedCategorized.activeBudgets[0] || null,
        }));
        setPastBudgets((prev) => ({
          ...prev,
          delegated: delegatedCategorized.pastBudgets,
        }));

        // For backward compatibility
        setDelegatedBudgetRequest(
          delegatedCategorized.activeBudgets[0] ||
            delegatedCategorized.pastBudgets[0] ||
            null,
        );
      }
    } catch (error) {
      console.error('❌ [BudgetTab] Error loading budget data:', error);
    } finally {
      setLoading(false);
    }
  }, [
    taskId,
    organizationId,
    task?.originalTaskId,
    task?.delegateTaskId,
    fetchBudgetRequestsByTaskId,
  ]);

  useEffect(() => {
    if (taskId && organizationId) {
      loadBudgetData();
    }
  }, [taskId, organizationId, loadBudgetData]);

  // Add effect to reload when task changes
  useEffect(() => {
    if (taskId && organizationId) {
      loadBudgetData();
    }
  }, [
    task?.delegateTaskId,
    task?.originalTaskId,
    task?.unassigned,
    task?.status,
    taskId,
    organizationId,
    loadBudgetData,
  ]);

  // Switch to active tab if user is on past tab but there are no past budgets
  useEffect(() => {
    const validPastBudgets = Array.from(
      new Map(
        [...pastBudgets.received] // Only include received past budgets, exclude delegated
          .filter(
            (budget) =>
              budget.status === BudgetRequestStatus.COMPLETED &&
              budget.delegationCancelledAt,
          )
          .map((budget) => [budget.budgetRequestId, budget]),
      ).values(),
    );
    if (budgetTabType === 'past' && validPastBudgets.length === 0) {
      setBudgetTabType('active');
    }
  }, [budgetTabType, pastBudgets.received]);

  // Set default tab based on task type
  useEffect(() => {
    const isDelegatedTask = !!task?.originalTaskId;
    const hasSubDelegation = !!task?.delegateTaskId;

    // For Company A (original delegator), default to 'delegated' tab
    // For Company B/C (sub-delegators or final recipients), default to 'received' tab
    if (!isDelegatedTask && hasSubDelegation) {
      setActiveSubTab('delegated');
    } else if (isDelegatedTask) {
      setActiveSubTab('received');
    }
  }, [task?.originalTaskId, task?.delegateTaskId]);

  // Check if this is a delegated task (created as a result of budget acceptance)
  const isDelegatedTask = !!task?.originalTaskId;

  // Check if this task has been sub-delegated
  const hasSubDelegation = !!task?.delegateTaskId;

  // Determine which budget request(s) to show based on context and budget tab type
  const currentBudgetRequests = (() => {
    if (budgetTabType === 'active') {
      // For active budgets, return single budget request
      // Company A (original delegator): Only show delegated budget
      if (!isDelegatedTask && hasSubDelegation) {
        return activeBudgets.delegated ? [activeBudgets.delegated] : [];
      }
      // Company C (final recipient): Only show received budget
      if (isDelegatedTask && !hasSubDelegation) {
        return activeBudgets.received ? [activeBudgets.received] : [];
      }
      // Company B (sub-delegator): Show based on active tab
      if (isDelegatedTask && hasSubDelegation) {
        const budget =
          activeSubTab === 'received'
            ? activeBudgets.received
            : activeBudgets.delegated;
        return budget ? [budget] : [];
      }
      // Fallback
      return activeBudgets.received ? [activeBudgets.received] : [];
    } else {
      // For past budgets, only show received past budgets (hide delegated past budgets)
      if (!isDelegatedTask && hasSubDelegation) {
        return []; // Hide past budgets for delegated context
      }
      if (isDelegatedTask && !hasSubDelegation) {
        return pastBudgets.received || [];
      }
      if (isDelegatedTask && hasSubDelegation) {
        return activeSubTab === 'received' ? pastBudgets.received || [] : []; // Hide past budgets for sub-delegated context
      }
      return pastBudgets.received || [];
    }
  })();

  // For backward compatibility, use the first budget request
  const currentBudgetRequest = currentBudgetRequests[0] || null;

  const pastBudgetsCount = Array.from(
    new Map(
      [...pastBudgets.received]
        .filter(
          (budget) =>
            budget.status === BudgetRequestStatus.COMPLETED &&
            budget.delegationCancelledAt,
        )
        .map((budget) => [budget.budgetRequestId, budget]),
    ).values(),
  ).length;

  const showPastBudgetNavigation =
    pastBudgetsCount > 0 && activeSubTab !== 'delegated';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500">{tTaskDetail.budget.loadingBudgetInfo}</div>
      </div>
    );
  }

  if (!receivedBudgetRequest && !delegatedBudgetRequest) {
    return (
      <EmptyBudgetState message={tTaskDetail.budget.noBudgetRequestsYet} />
    );
  }

  return (
    <div className="space-y-6 max-h-full overflow-y-auto">
      {/* Sub-tab Navigation (only show if both received and delegated exist) */}
      {isDelegatedTask && hasSubDelegation && (
        <BudgetSubTabNavigation
          activeSubTab={activeSubTab}
          setActiveSubTab={setActiveSubTab}
        />
      )}

      {/* Cancelled Delegation Budgets Section - moved to top */}
      <CancelledBudgets cancelledBudgetRequests={cancelledBudgetRequests} />

      {/* Active vs Past Budget Tab Navigation */}
      {showPastBudgetNavigation && (
        <BudgetTabNavigation
          budgetTabType={budgetTabType}
          setBudgetTabType={setBudgetTabType}
          pastBudgetsCount={pastBudgetsCount}
        />
      )}

      {/* Header - Hide when showing multiple past budgets */}
      {!(budgetTabType === 'past' && currentBudgetRequests.length > 1) && (
        <BudgetHeader
          activeSubTab={activeSubTab}
          isDelegatedTask={isDelegatedTask}
          hasSubDelegation={hasSubDelegation}
          currentBudgetRequest={currentBudgetRequest}
          isCompletedCancelledBudget={isCompletedCancelledBudget}
        />
      )}

      {/* Task and Project Information - Hide when showing multiple past budgets */}
      {!(budgetTabType === 'past' && currentBudgetRequests.length > 1) && (
        <TaskProjectInfo
          activeSubTab={activeSubTab}
          isDelegatedTask={isDelegatedTask}
          task={task}
          currentBudgetRequest={currentBudgetRequest}
          organizationId={organizationId || ''}
          isRecipient={isRecipient}
        />
      )}

      {/* Delegation Info (only for received tab of delegated tasks) */}
      {!(budgetTabType === 'past' && currentBudgetRequests.length > 1) &&
        activeSubTab === 'received' &&
        isDelegatedTask && (
          <DelegationInfo
            task={task}
            currentBudgetRequest={currentBudgetRequest}
          />
        )}

      {/* Budget Details or Empty State */}
      {currentBudgetRequests.length > 0 ? (
        budgetTabType === 'past' && currentBudgetRequests.length > 1 ? (
          // Multiple past budgets - show all of them
          <div className="space-y-4">
            {currentBudgetRequests.map((budgetRequest, index) => (
              <BudgetRequestCard
                key={budgetRequest.budgetRequestId}
                budgetRequest={budgetRequest}
                activeSubTab={activeSubTab}
                isDelegatedTask={isDelegatedTask}
                isCompletedCancelledBudget={isCompletedCancelledBudget}
                title={
                  activeSubTab === 'delegated'
                    ? !isDelegatedTask
                      ? tTaskDetail.budget.delegationBudgetNumber.replace('{n}', String(index + 1))
                      : tTaskDetail.budget.subDelegationBudgetNumber.replace(
                          '{n}',
                          String(index + 1),
                        )
                    : activeSubTab === 'received' && isDelegatedTask
                      ? tTaskDetail.budget.originalBudgetNumber.replace('{n}', String(index + 1))
                      : tTaskDetail.budget.budgetNumber.replace('{n}', String(index + 1))
                }
              />
            ))}
          </div>
        ) : (
          // Single budget (active or single past budget)
          <BudgetRequestDetails
            budgetRequest={currentBudgetRequest}
            activeSubTab={activeSubTab}
            isDelegatedTask={isDelegatedTask}
          />
        )
      ) : // Empty state when no budget request is found for the current tab
      activeSubTab === 'delegated' && hasSubDelegation ? (
        <EmptyBudgetState
          isDelegatedTask={isDelegatedTask}
          hasSubDelegation={hasSubDelegation}
          task={task}
        />
      ) : null}

      {/* Action Button - Only show for single budget displays */}
      {currentBudgetRequest &&
        !(budgetTabType === 'past' && currentBudgetRequests.length > 1) && (
          <div className="flex flex-wrap justify-center gap-2 px-4 py-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                window.open(
                  `/budgets?budgetRequestId=${currentBudgetRequest.budgetRequestId}`,
                  '_blank',
                );
              }}>
              <RequestQuote fontSize="small" className="mr-2" />
              {tTaskDetail.budget.viewFullHistory}
            </Button>
          </div>
        )}
    </div>
  );
};
